import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RegisterStatus } from '../../../../core/models/register.model';

@Injectable({
  providedIn: 'root'
})
export class RegisterValidationService {
  // Key for storing employee opening dates in localStorage
  private readonly EMPLOYEE_OPENING_DATES_KEY = 'employee_opening_dates';

  constructor(private dialog: MatDialog) {}

  /**
   * Gets the stored employee opening dates from localStorage
   * @returns A map of employee IDs to their last opening dates
   */
  private getEmployeeOpeningDates(): { [employeeId: string]: string } {
    const storedData = localStorage.getItem(this.EMPLOYEE_OPENING_DATES_KEY);
    return storedData ? JSON.parse(storedData) : {};
  }

  /**
   * Sets the last opening date for an employee
   * @param employeeId The ID of the employee
   * @param date The date of the opening (defaults to current date)
   */
  public setLastOpeningDate(employeeId: string, date: Date = new Date()): void {
    const openingDates = this.getEmployeeOpeningDates();
    openingDates[employeeId] = date.toISOString();
    localStorage.setItem(this.EMPLOYEE_OPENING_DATES_KEY, JSON.stringify(openingDates));
  }

  /**
   * Checks if a new opening is required for the employee
   * @param employeeId The ID of the employee
   * @returns True if a new opening is required, false otherwise
   */
  private isNewOpeningRequired(employeeId: string): boolean {
    const openingDates = this.getEmployeeOpeningDates();
    const lastOpeningDate = openingDates[employeeId];

    // If no last opening date, opening is required
    if (!lastOpeningDate) {
      return true;
    }

    const lastOpening = new Date(lastOpeningDate);
    const currentDate = new Date();

    localStorage.getItem('closingStatus');
    const isClosed = Number(localStorage.getItem('closingStatus')) === RegisterStatus.NONE_OPEN;

    // Check if the current date is a different day than the last opening
    // This means midnight has passed since the last opening
    return (
      lastOpening.getDate() !== currentDate.getDate() ||
      lastOpening.getMonth() !== currentDate.getMonth() ||
      lastOpening.getFullYear() !== currentDate.getFullYear()
    );
  }

  /**
   * Checks if the current user needs to perform a new opening
   * @returns True if a new opening is required, false otherwise
   */
  public checkIfNewOpeningRequired(): boolean {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      return false; // No user logged in
    }
    const employeeId = user.id_funcionario;
    return this.isNewOpeningRequired(employeeId);
  }

  /**
   * Opens the register dialog in a non-closable mode
   * @param componentType The component type to open in the dialog
   * @returns A MatDialogRef instance
   */
  public openRegisterDialog(componentType: any): MatDialogRef<any> {
    const dialogRef = this.dialog.open(componentType, {
      width: '400px',
      disableClose: true, // Prevent closing by clicking outside or pressing escape
      data: {
        isRequired: true
      }
    });

    return dialogRef;
  }
}
